import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { api } from '@/lib/api';
import LivingBubble from '@/components/LivingBubble';
import LifeClock from '@/components/LifeClock';
import BubbleEye from '@/components/BubbleEye';
import BubbleVoice from '@/components/BubbleVoice';
import { track } from '@/lib/track';
import ConversationOrb from '@/components/ConversationOrb';
import SuggestionCard from '@/components/SuggestionCard';
import ShakeBlowDeck from '@/components/ShakeBlowDeck';
import { useAuth } from '@/lib/auth';
import { Lightning, Sparkle, Users, Clock, ArrowRight, Crown, Eye, Heart, ShareNetwork, Palette } from '@phosphor-icons/react';
import BubbleStories from '@/components/BubbleStories';
import LevelBadge from '@/components/LevelBadge';
import CargoPanel from '@/components/CargoPanel';
import BubbleRelationships from '@/components/BubbleRelationships';
import PulsePanel from '@/components/PulsePanel';
import CosmeticsDrawer from '@/components/CosmeticsDrawer';
import BubbleJournal from '@/components/BubbleJournal';
import BubbleDiary from '@/components/BubbleDiary';
import BubbleAlmanac from '@/components/BubbleAlmanac';
import FounderBadge from '@/components/FounderBadge';
import MemoryPanel from '@/components/MemoryPanel';
import CreatorFeed from '@/components/CreatorFeed';
import CreatorLinksEditor from '@/components/CreatorLinksEditor';

const Stat = ({ label, value, color = 'text-bx-cyan' }) => (
  <div className="glass rounded-xl p-4">
    <div className="text-[10px] uppercase tracking-widest text-white/60">{label}</div>
    <div className={`font-display text-2xl font-black mt-1 ${color}`}>{value}</div>
  </div>
);

const HistoryItem = ({ h }) => {
  const isPass = h.type === 'passed';
  return (
    <div className="flex items-start gap-3 py-3 border-b border-white/5 last:border-b-0">
      <div className={`mt-1 h-2 w-2 rounded-full ${isPass ? 'bg-bx-magenta' : 'bg-bx-volt'}`} />
      <div className="flex-1">
        <div className="text-sm">
          <span className="font-bold text-white">{h.type}</span>{' '}
          {isPass ? <>by <span className="text-bx-cyan">@{h.from}</span> → <span className="text-bx-magenta">@{h.to}</span></> :
            <span className="text-white/70">by @{h.by}</span>}
        </div>
        {h.note && <div className="text-white/65 text-xs mt-0.5">{h.note}</div>}
        {h.message && <div className="text-white/80 text-xs italic mt-0.5">"{h.message}"</div>}
      </div>
      <div className="text-[10px] uppercase tracking-widest text-white/55">
        {new Date(h.ts).toLocaleString()}
      </div>
    </div>
  );
};

const BubbleDetail = () => {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [bubble, setBubble] = useState(null);
  const [owner, setOwner] = useState(null);
  const [busy, setBusy] = useState(false);
  const [following, setFollowing] = useState(false);
  const [closeFriendStatus, setCloseFriendStatus] = useState(null); // null | 'pending' | 'active'
  const [eyeOpen, setEyeOpen] = useState(false);
  const [convOpen, setConvOpen] = useState(false);
  const [decorateOpen, setDecorateOpen] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/bubbles/${id}`);
    setBubble(data.bubble);
    setOwner(data.owner);
    setFollowing(!!data.bubble?.is_following);
    if (data.bubble?.owner_id && data.bubble.owner_id !== user?.id) track('world_interaction', { kind: 'view_bubble' });
  };

  const loadCloseFriendStatus = async () => {
    if (!user) return;
    try {
      const { data } = await api.get(`/bubbles/${id}/close-friend-status`);
      setCloseFriendStatus(data.status);
    } catch (_) {}
  };

  useEffect(() => { load(); loadCloseFriendStatus(); }, [id]); // eslint-disable-line

  const toggleFollow = async () => {
    try {
      if (following) {
        await api.delete(`/bubbles/${id}/follow`);
        setFollowing(false);
        toast.message('unfollowed');
      } else {
        await api.post(`/bubbles/${id}/follow`);
        setFollowing(true);
        toast.success('following');
      }
      load();
    } catch (e) { toast.error('could not follow'); }
  };

  const requestCloseFriend = async () => {
    try {
      setBusy(true);
      const { data } = await api.post(`/bubbles/${id}/close-friend`, { message: '' });
      setCloseFriendStatus(data.status);
      toast.success(data.status === 'active' ? 'Inner circle joined!' : 'Close connection requested');
    } catch (e) { toast.error(e?.response?.data?.detail || 'could not send request'); }
    finally { setBusy(false); }
  };

  const sharePublic = () => {
    const url = `${window.location.origin}/b/${id}`;
    navigator.clipboard.writeText(url);
    toast.success('Public link copied');
  };

  if (!bubble) return <div className="text-center py-20 text-white/65">loading bubble…</div>;

  const isOwner = user?.id === bubble.owner_id;

  const evolve = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/bubbles/${id}/evolve`);
      setBubble(data.bubble);
      toast.success('Bubble evolved');
    } catch (err) { toast.error(err?.response?.data?.detail || 'cannot evolve'); }
    finally { setBusy(false); }
  };

  const discover = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/bubbles/${id}/discover`);
      if (data.revealed) {
        toast.success(`Hidden trait revealed: ${data.personality}`);
        load();
      } else {
        toast.message(data.hint || 'nothing yet… try again later');
      }
    } catch (err) { toast.error(err?.response?.data?.detail || 'cannot listen now'); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-8 py-10 pb-24">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-5">
          <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.4 }}>
            <LivingBubble bubble={bubble} mood={bubble.mood === 'sad' ? 'sad' : 'happy'} className="w-full max-w-md mx-auto" testid="detail-living-bubble" />
          </motion.div>
        </div>

        <div className="lg:col-span-7 space-y-6">
          <div>
            <div className="flex items-center gap-2">
              <span className={`chip rar-${bubble.rarity}`}>{bubble.rarity}</span>
              <span className="chip">{bubble.trait}</span>
              <span className="chip text-bx-volt">{bubble.mood}</span>
              <span className="chip"><Crown size={12} weight="fill" /> stage {bubble.evolution_stage}</span>
            </div>
            <h1 className="font-display text-4xl sm:text-6xl font-black tracking-tighter mt-3">{bubble.name}</h1>
            <div className="mt-2"><LevelBadge bubble={bubble} size="lg" /></div>
            <p className="text-white/70 mt-2 text-base sm:text-lg">"{bubble.personality?.bio}"</p>
            <div className="mt-3 text-sm text-white/55">
              <span className="text-bx-cyan font-bold">{bubble.personality?.catchphrase}</span> · loves {bubble.personality?.fav_action}
            </div>
            {owner && (
              <div className="mt-3 text-sm text-white/60">
                Currently held by <Link to="/profile" className="text-bx-magenta font-bold">@{owner.username}</Link>
                {' · '}created by @{bubble.creator_username}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat label="Energy" value={`${bubble.energy}/100`} color="text-bx-cyan" />
            <Stat label="Velocity" value={bubble.velocity} color="text-bx-volt" />
            <Stat label="Transfers" value={bubble.transfers} color="text-bx-magenta" />
            <Stat label="Followers" value={bubble.followers_count ?? (bubble.followers || []).length} color="text-bx-gold" />
          </div>

          {(bubble.parent_name || bubble.child_name) && (
            <div className="grad-border p-4">
              <div className="text-[10px] uppercase tracking-widest text-bx-gold flex items-center gap-1">
                <Crown size={12} weight="fill" /> Bloodline
              </div>
              {bubble.parent_name && (
                <div className="text-sm text-white/80 mt-1">
                  Heir of <Link to={`/b/${bubble.parent_id}`} className="text-bx-cyan font-bold">{bubble.parent_name}</Link>.
                </div>
              )}
              {bubble.child_name && (
                <div className="text-sm text-white/80 mt-1">
                  Pulse continues in <Link to={`/b/${bubble.child_id}`} className="text-bx-cyan font-bold">{bubble.child_name}</Link>.
                </div>
              )}
            </div>
          )}

          <div className="flex flex-wrap gap-3">
            <button onClick={toggleFollow} data-testid="bubble-follow-btn"
                    className={`px-5 py-3 rounded-full text-sm font-bold flex items-center gap-2 transition ${
                      following ? 'glass-hi' : 'btn-volt'
                    }`}>
              <Heart size={14} weight={following ? 'fill' : 'bold'} />
              {following ? 'Following' : 'Follow'}
            </button>
            {!isOwner && (
              <button onClick={requestCloseFriend} disabled={busy || closeFriendStatus === 'pending' || closeFriendStatus === 'active'}
                      data-testid="bubble-close-friend-btn"
                      className={`px-5 py-3 rounded-full text-sm font-bold flex items-center gap-2 transition ${
                        closeFriendStatus === 'active' ? 'glass-hi text-bx-volt' :
                        closeFriendStatus === 'pending' ? 'glass-hi text-white/50' : 'glass-hi hover:bg-white/10'
                      } disabled:cursor-default`}>
                <Users size={14} weight={closeFriendStatus === 'active' ? 'fill' : 'bold'} />
                {closeFriendStatus === 'active' ? 'Inner Circle' : closeFriendStatus === 'pending' ? 'Pending…' : 'Join Inner Circle'}
              </button>
            )}
            <button onClick={sharePublic} data-testid="bubble-share-btn"
                    className="px-5 py-3 rounded-full glass-hi text-sm font-bold flex items-center gap-2 hover:bg-white/10">
              <ShareNetwork size={14} weight="bold" /> Share
            </button>
          </div>

          {!isOwner && (
            <button data-testid="bubble-talk-btn-visitor"
                    onClick={() => setConvOpen(true)}
                    className="btn-volt w-full py-3 rounded-full text-sm font-bold flex items-center justify-center gap-2">
              Talk to {bubble.name}
            </button>
          )}

          {isOwner && (
            <div className="flex flex-wrap gap-3">
              <button data-testid="bubble-pass-btn" onClick={() => nav(`/pass/${bubble.id}`)} className="btn-primary px-6 py-3 rounded-full text-sm flex items-center gap-2">
                Pass this Bubble <ArrowRight size={14} weight="bold" />
              </button>
              <button data-testid="bubble-brush-btn" onClick={() => nav(`/brush/${bubble.id}`)}
                      className="px-6 py-3 rounded-full glass-hi text-sm font-bold hover:bg-white/10 transition flex items-center gap-2">
                <Heart size={14} weight="fill" className="text-bx-magenta" /> Brush without taking
              </button>
              <button
                data-testid="bubble-evolve-btn"
                onClick={evolve}
                disabled={busy || bubble.energy < 80}
                className="btn-volt px-6 py-3 rounded-full text-sm flex items-center gap-2 disabled:opacity-40"
              >
                <Sparkle size={14} weight="fill" /> Evolve {bubble.energy < 80 && '(need 80⚡)'}
              </button>
              <button
                data-testid="bubble-discover-btn"
                onClick={discover}
                disabled={busy}
                className="px-6 py-3 rounded-full glass-hi text-sm font-bold hover:bg-white/10 transition disabled:opacity-40 flex items-center gap-2"
              >
                <Eye size={14} weight="bold" /> Listen for the secret
              </button>
              <button
                data-testid="bubble-eye-btn"
                onClick={() => setEyeOpen(true)}
                className="px-6 py-3 rounded-full glass-hi text-sm font-bold hover:bg-white/10 transition flex items-center gap-2"
              >
                <Eye size={14} weight="fill" className="text-bx-cyan" /> Show me your world
              </button>
              <button
                data-testid="bubble-talk-btn"
                onClick={() => setConvOpen(true)}
                className="btn-volt px-6 py-3 rounded-full text-sm font-bold flex items-center gap-2"
              >
                Talk to {bubble.name}
              </button>
              <button
                data-testid="bubble-decorate-btn"
                onClick={() => setDecorateOpen(true)}
                className="px-6 py-3 rounded-full glass-hi text-sm font-bold hover:bg-white/10 transition flex items-center gap-2"
              >
                <Palette size={14} weight="fill" className="text-bx-magenta" /> Decorate
              </button>
              <BubbleVoice bubble={bubble} isOwner={true} onUpdate={load} />
            </div>
          )}

          <SuggestionCard bubble={bubble} onUpdate={load} />

          <CreatorFeed bubbleId={bubble.id} />

          <CargoPanel bubble={bubble} isOwner={isOwner} onUpdate={load} />

          <PulsePanel bubble={bubble} isOwner={isOwner} />

          <BubbleRelationships bubble={bubble} isOwner={isOwner} onUpdate={load} />

          <BubbleJournal bubble={bubble} isOwner={isOwner} />

          {isOwner && <BubbleAlmanac bubble={bubble} />}

          {isOwner && <MemoryPanel bubbleId={bubble.id} />}

          {isOwner && (
            <CreatorLinksEditor bubbleId={bubble.id} initialLinks={bubble.creator_links || []} />
          )}

          <BubbleDiary bubble={bubble} isOwner={isOwner} />

          <ShakeBlowDeck bubble={bubble} onUpdate={load} />

          <CosmeticsDrawer
            open={decorateOpen}
            bubble={bubble}
            onClose={() => setDecorateOpen(false)}
            onApplied={load}
          />

          <ConversationOrb
            bubble={bubble}
            open={convOpen}
            onClose={() => setConvOpen(false)}
          />

          <BubbleEye
            bubble={bubble}
            open={eyeOpen}
            onClose={() => setEyeOpen(false)}
            onObservation={() => load()}
          />

          <LifeClock bubble={bubble} />

          {bubble.secret_revealed && bubble.secret_personality && (
            <div className="grad-border p-4">
              <div className="text-[10px] uppercase tracking-widest text-bx-gold">Hidden personality discovered</div>
              <div className="font-display text-2xl font-black mt-1 shimmer-text">{bubble.secret_personality}</div>
            </div>
          )}

          <BubbleTabs bubble={bubble} />

          <div className="text-xs text-white/60 flex items-center gap-2">
            <Lightning size={12} weight="fill" className="text-bx-volt" />
            All moves are recorded on the BubbleX transparent ledger.
            <Link to="/ledger" data-testid="bubble-to-ledger" className="text-bx-cyan ml-1">verify →</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

const BubbleTabs = ({ bubble }) => {
  const [tab, setTab] = React.useState('stories');
  return (
    <div data-testid="bubble-tabs">
      <div className="flex gap-2 mb-4 border-b border-white/10">
        {[
          { id: 'stories',  label: 'Stories' },
          { id: 'timeline', label: 'Timeline' },
        ].map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)}
                  data-testid={`tab-${t.id}`}
                  className={`px-4 py-2 text-xs uppercase tracking-widest font-bold transition border-b-2 ${
                    tab === t.id ? 'border-bx-cyan text-bx-cyan'
                                 : 'border-transparent text-white/60 hover:text-white/70'
                  }`}>
            {t.label}
          </button>
        ))}
      </div>
      {tab === 'stories' && <BubbleStories bubbleId={bubble.id} />}
      {tab === 'timeline' && (
        <div className="glass rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <Clock size={16} className="text-bx-cyan" weight="duotone" />
            <h3 className="font-display font-bold">Bubble timeline</h3>
          </div>
          <div className="max-h-[380px] overflow-y-auto">
            {(bubble.history || []).slice().reverse().map((h, idx) => (
              <HistoryItem key={idx} h={h} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default BubbleDetail;
